/**
 * Curated catalog of Lucide icon names eligible for use as a tenant's
 * `assets.brandSymbol`. Shared between the frontend BrandSymbol component
 * (which renders the icon in image placeholders) and the wizard backend
 * (which asks the AI to pick one for new tenants).
 *
 * Picks are constrained to this list so the AI can't return an icon name
 * that doesn't render and so we don't pull every Lucide icon into the
 * frontend bundle. Add new icons here when a tenant needs one — also add
 * them to the ICON_MAP in web/src/components/BrandSymbol.tsx.
 */

export const BRAND_SYMBOL_FALLBACK = "Sparkles";

export const BRAND_SYMBOL_CATALOG = [
  // Nature / outdoors
  "Leaf",
  "Sprout",
  "TreeDeciduous",
  "TreePine",
  "Flower2",
  "Sun",
  "Sunrise",
  "Mountain",
  "Tent",
  "TentTree",
  "Compass",
  "Map",
  "Anchor",
  "Ship",
  "Waves",

  // Animals
  "Cat",
  "Dog",
  "PawPrint",
  "Bird",
  "Fish",
  "Rabbit",

  // Food & drink
  "Coffee",
  "Cake",
  "Pizza",
  "Soup",
  "Utensils",
  "Drumstick",
  "Wine",
  "Beer",
  "IceCream",
  "Carrot",

  // Apparel & lifestyle
  "Shirt",
  "Watch",
  "Glasses",
  "Footprints",
  "Gem",
  "Crown",

  // Crafts / tools / makers
  "Hammer",
  "Wrench",
  "Scissors",
  "Pencil",
  "PenTool",
  "Palette",
  "Paintbrush",
  "Brush",
  "Drill",
  "Ruler",

  // Tech / vehicles
  "Plane",
  "Bike",
  "Car",
  "Truck",
  "Rocket",
  "Cpu",
  "Camera",
  "Music",
  "Gamepad2",
  "Headphones",
  "Smartphone",
  "Layers",
  "Box",
  "Package",

  // Civic / institutional / education
  "Landmark",
  "BookOpen",
  "GraduationCap",
  "Library",
  "Microscope",
  "Beaker",
  "Globe",

  // Health / wellness
  "Heart",
  "Stethoscope",
  "Pill",
  "Dumbbell",

  // Home / decor
  "Home",
  "Lamp",
  "Sofa",
  "Bed",
  "Armchair",

  // Generic shop / retail
  "ShoppingBag",
  "ShoppingCart",
  "Store",
  "Tag",
  "Gift",
  "Trophy",
  "Award",
  "Star",
  "Sparkles",
  "Flame",
  "Hexagon",
  "Diamond",
] as const;

export type BrandSymbolName = (typeof BRAND_SYMBOL_CATALOG)[number];

const CATALOG_SET: Set<string> = new Set(BRAND_SYMBOL_CATALOG);

export function isBrandSymbolName(name: string): name is BrandSymbolName {
  return CATALOG_SET.has(name);
}

export function normalizeBrandSymbol(name: string | undefined | null): BrandSymbolName {
  if (name && isBrandSymbolName(name)) return name;
  return BRAND_SYMBOL_FALLBACK;
}
